import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { trackLogin, trackSignup, setUserProperties, setUserPlan } from "../utils/analytics";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem("tally_token"));

  const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: {
      "Content-Type": "application/json",
    },
  });

  // Add auth header to requests
  api.interceptors.request.use((config) => {
    const storedToken = localStorage.getItem("tally_token");
    if (storedToken) {
      config.headers.Authorization = `Bearer ${storedToken}`;
    }
    return config;
  });

  // Handle auth errors
  api.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        logout();
      }
      return Promise.reject(error);
    }
  );

  const fetchUser = useCallback(async () => {
    const storedToken = localStorage.getItem("tally_token");
    if (!storedToken) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get("/auth/me");
      setUser(response.data);
    } catch (error) {
      console.error("Failed to fetch user:", error);
      localStorage.removeItem("tally_token");
      setToken(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await api.post("/auth/login", { email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem("tally_token", access_token);
    setToken(access_token);
    setUser(userData);
    
    // Track login event
    trackLogin('email');
    setUserProperties(userData.id, {
      email: userData.email,
      plan: userData.plan,
      currency: userData.currency
    });
    setUserPlan(userData.plan);
    
    return userData;
  };

  const signup = async (email, password, name, timezone = "America/Toronto", currency = "CAD", birthdate = null, plan = null) => {
    // Check for affiliate referral code
    const ref = localStorage.getItem("tally_ref") || null;
    const response = await api.post("/auth/signup", { 
      email, 
      password, 
      name,
      timezone,
      currency,
      birthdate,
      plan,
      ref
    });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem("tally_token", access_token);
    setToken(access_token);
    setUser(userData);
    
    // Track signup conversion
    trackSignup('email', userData.plan || plan);
    setUserProperties(userData.id, {
      email: userData.email,
      plan: userData.plan,
      currency: userData.currency
    });
    setUserPlan(userData.plan);
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("tally_token");
    setToken(null);
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  // Check if user has access to premium features
  const hasPremiumAccess = () => {
    if (!user) return false;
    // Student and Senior plans have full access (free)
    // Power plan has full access if active
    return user.plan === "student" || user.plan === "senior" || 
           (user.plan === "power" && user.plan_status === "active");
  };

  // Check if user is on student plan (limited features)
  const isStudentPlan = () => {
    return user?.plan === "student";
  };

  const value = {
    user,
    token,
    loading,
    login,
    signup,
    logout,
    updateUser,
    api,
    isAuthenticated: !!user,
    hasPremiumAccess,
    isStudentPlan,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
